import { Link } from 'react-router-dom';
import type { PersonProjectAllocation } from '../../lib/peopleApi';

interface Props {
  allocations: PersonProjectAllocation[];
}

// Health dot colors (ProjectHealth: ON_TRACK / AT_RISK / OFF_TRACK).
const HEALTH_DOT: Record<string, string> = {
  ON_TRACK: 'bg-green-500',
  AT_RISK: 'bg-yellow-500',
  OFF_TRACK: 'bg-red-500',
};

const HEALTH_LABEL: Record<string, string> = {
  ON_TRACK: 'În grafic',
  AT_RISK: 'În risc',
  OFF_TRACK: 'În întârziere',
};

function statusLabel(status: string | null): string {
  if (!status) return '—';
  return status
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function HealthBadge({ health }: { health: string | null }) {
  if (!health) return <span className="text-gray-400">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5 text-gray-700">
      <span className={`h-2 w-2 rounded-full ${HEALTH_DOT[health] ?? 'bg-gray-300'}`} />
      {HEALTH_LABEL[health] ?? health}
    </span>
  );
}

/**
 * Project allocations derived from a person's built-in ticketing workload
 * (CLE-155): one row per project the person has active tasks on, with project
 * status, health, role and active-task count. Projects matched to a local
 * project link to its dashboard page.
 */
export function PersonProjectAllocations({ allocations }: Props) {
  if (allocations.length === 0) {
    return <p className="text-sm text-gray-500">Nicio alocare activă pe proiecte.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-gray-500">
            <th className="py-2 font-medium">Proiect</th>
            <th className="py-2 font-medium">Status</th>
            <th className="py-2 font-medium">Stare</th>
            <th className="py-2 font-medium">Rol</th>
            <th className="py-2 text-right font-medium">Sarcini active</th>
          </tr>
        </thead>
        <tbody>
          {allocations.map((a) => (
            <tr key={a.projectId ?? a.projectName} className="border-b border-gray-100">
              <td className="py-2 text-gray-900">
                {a.projectId ? (
                  <Link
                    to={`/projects/${a.projectId}`}
                    className="font-medium text-brand-600 hover:underline"
                  >
                    {a.projectName}
                  </Link>
                ) : (
                  <span className="font-medium">{a.projectName}</span>
                )}
                {a.projectCode && (
                  <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">
                    {a.projectCode}
                  </span>
                )}
              </td>
              <td className="py-2 text-gray-600">{statusLabel(a.status)}</td>
              <td className="py-2 text-gray-600">
                <HealthBadge health={a.health} />
              </td>
              <td className="py-2 text-gray-600">{a.role ? statusLabel(a.role) : '—'}</td>
              <td className="py-2 text-right font-semibold text-gray-900">{a.activeTaskCount}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
